import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PACKAGES } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { full_name: "", email: "", organization: "", phone: "", package: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, package: e.detail }));
    window.addEventListener("select-package", handler);
    return () => window.removeEventListener("select-package", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.package) {
      toast.error("Please fill in your name, email, and select a package.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/service-requests`, form);
      toast.success("Request received. Our enrollment team will reach out within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors duration-200";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 bg-secondary">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Get started</span>
          <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none text-foreground">
            Request your enrollment consultation.
          </h2>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-md">
            Tell us about your providers and payer goals. We'll respond with a no-frills quote and no-pressure sales calls!
          </p>
          <div className="mt-10 space-y-4">
            <a href="mailto:hello@alignesllc.com" data-testid="contact-email-link" className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors duration-200">
              <Mail size={16} /> hello@alignesllc.com
            </a>
            <a href="tel:+16314151476" data-testid="contact-phone-link" className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors duration-200">
              <Phone size={16} /> (631) 415-1476
            </a>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8"
          data-testid="contact-form"
        >
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Full Name *</label>
            <input data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" className={inputCls} />
          </div>
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email *</label>
            <input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@practice.com" className={inputCls} />
          </div>
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Organization</label>
            <input data-testid="input-organization" value={form.organization} onChange={set("organization")} placeholder="Practice / Group" className={inputCls} />
          </div>
          <div className="sm:col-span-1">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Phone</label>
            <input data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Package of Interest *</label>
            <Select value={form.package} onValueChange={(v) => setForm((f) => ({ ...f, package: v }))}>
              <SelectTrigger data-testid="select-package" className="w-full mt-2 rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0">
                <SelectValue placeholder="Select a package" />
              </SelectTrigger>
              <SelectContent>
                {PACKAGES.map((p) => (
                  <SelectItem key={p.name} value={p.name} data-testid={`option-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
                    {p.name} — {p.tag}
                  </SelectItem>
                ))}
                <SelectItem value="Not sure yet">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Message</label>
            <textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={3} placeholder="Tell us about your providers and payer goals…" className={`${inputCls} resize-none`} />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              data-testid="submit-request-button"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Submit Request"} <ArrowRight size={16} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
